import { useEffect, useState, type CSSProperties } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { errorMessage } from '@/lib/api'
import { useToast } from '@/components/ui/toast'
import QRCode from 'react-qr-code'
import { ArrowLeft, FileSpreadsheet, Inbox, Printer, Send } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, formatWeekday } from '@/lib/date'
import { FREE_TEXT_FIELDS, FREE_TEXT_SPEC_KEYS, specEntries, specSummary, useOptionGroups, type SpecEntry } from '@/lib/options'
import { fileSize, itemArtworks } from '@/lib/artwork'
import { money } from '@/lib/money'
import { downloadDesignerExcel } from '@/lib/designerExcel'
import { workedBy, workedByShort } from '@/lib/stageLog'
import type { DepartmentDeadline, Job, OptionGroup, Order, OrderItem, StageLog } from '@/types'
import Button from '@/components/ui/Button'
import LogoLoader from '@/components/ui/LogoLoader'
import ArtworkPreview from '@/components/order/ArtworkPreview'
import UrgentBadge from '@/components/job/UrgentBadge'
import PaymentBadge from '@/components/order/PaymentBadge'
import { invalidateOrderViews } from '@/components/order/lifecycle'
import ProceedConfirmDialog from '@/components/order/ProceedConfirmDialog'

/**
 * What the floor calls this job, printed at the top of the sheet.
 *
 * The fallback stays here rather than in the API on purpose: a server-derived
 * job_name could never be told apart from one a person typed, so an order
 * would arrive at any future edit form pre-filled with a guess. What a blank
 * title should read as on paper is a print concern, and this is the print.
 */
function sheetTitle(order?: Order | null, fallback = ''): string {
  const named = order?.job_name?.trim()
  if (named) return named

  const products = [...new Set((order?.items ?? []).map((item) => item.product).filter(Boolean))]
  if (products.length === 0) return order?.number ?? fallback

  const shown = products.slice(0, 2).join(' + ')
  const more = products.length > 2 ? ` +${products.length - 2} more` : ''

  return `${shown}${more} - ${order?.total_pieces ?? 0}PCS`
}

/**
 * Artwork paths are root-relative while uploads live on the local disk and
 * absolute once they move to object storage, and a printed QR needs a whole
 * URL either way.
 */
function absoluteUrl(path: string): string {
  return new URL(path, window.location.origin).href
}

const LETTER_SIZES = ['XXS', '2XS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL', '4XL', '5XL', '6XL', '7XL', '8XL']

/**
 * Size column order: numeric (kids) sizes first, then XS to 8XL, the way the
 * CetakPro sheet lists them. size_matrix keeps the order sizes were typed in,
 * which printed L above M. Anything else keeps its place at the end.
 */
function sizeRank(size: string): number {
  const label = size.trim().toUpperCase()
  if (/^\d+$/.test(label)) return Number(label)
  const letter = LETTER_SIZES.indexOf(label)

  return letter >= 0 ? 1000 + letter : 2000
}

/** A labelled box, the unit the floor reads the sheet in. Blank prints as a write-in line. */
function SpecBox({ label, value, image }: { label: string; value?: string | null; image?: string | null }) {
  return (
    <div className="break-inside-avoid">
      <p className="text-2xs font-bold uppercase">{label}</p>
      <div className="mt-0.5 rounded border-2 border-navy px-2 py-1 text-center">
        {image && <OptionPicture src={image} className="mx-auto mb-0.5 h-14" />}
        <p className="min-h-[1lh] text-sm font-extrabold uppercase leading-tight break-words">{value || ''}</p>
      </div>
    </div>
  )
}

function OptionPicture({ src, className }: { src: string; className: string }) {
  return (
    <img
      src={src}
      alt=""
      className={cn('w-auto max-w-full object-contain', className)}
      onError={(e) => {
        ;(e.target as HTMLImageElement).style.display = 'none'
      }}
    />
  )
}

/** What a panel prints about one item: its files, options, free-text write-ins and sizes in size order. */
function panelData(item: OrderItem, groups: OptionGroup[] | undefined) {
  return {
    files: itemArtworks(item).map((file) => ({ ...file, url: file.path ? absoluteUrl(file.path) : null })),
    options: specEntries(item.spec, groups, FREE_TEXT_SPEC_KEYS),
    freeText: FREE_TEXT_FIELDS.map((field): SpecEntry => ({ key: field.key, label: field.label, value: item.spec?.[field.key] ?? '' })),
    sizes: Object.entries(item.size_matrix ?? {}).filter(([, qty]) => qty > 0).sort(([a], [b]) => sizeRank(a) - sizeRank(b)),
  }
}

type SheetFile = ReturnType<typeof panelData>['files'][number]

/**
 * What the front panel says about an item's names, so the floor never has to
 * guess from the mockup (Duncan 2026-11-03: a 2-pc item with no names beside a
 * 26-name one). Only asked when some item on the order has names.
 */
function namesNote(item: OrderItem): string {
  const count = item.personalizations?.length ?? 0
  if (count === 0) return 'NO NAME'
  if (count >= item.qty) return `${count} NAMES - BACK PAGE`

  return `${count} OF ${item.qty} NAMED - CHECK`
}

/** Two across for 2-4 pictures, three across for 5-6, so front and back sit side by side. */
const MOCKUP_GRID: Record<number, string> = {
  1: 'grid-cols-1 print:grid-rows-[minmax(0,1fr)]',
  2: 'grid-cols-2 print:grid-rows-[minmax(0,1fr)]',
  3: 'grid-cols-2 print:grid-rows-[repeat(2,minmax(0,1fr))]',
  4: 'grid-cols-2 print:grid-rows-[repeat(2,minmax(0,1fr))]',
  5: 'grid-cols-3 print:grid-rows-[repeat(2,minmax(0,1fr))]',
  6: 'grid-cols-3 print:grid-rows-[repeat(2,minmax(0,1fr))]',
}

/**
 * Every picture of the item, in the customer's order (front, back, ...). On a
 * one-item sheet the area takes all the height its panel has left and the
 * pictures share it, so a fixed cap can neither waste the page nor push the
 * panel onto page 2. `fill` is off when several items flow down the page and
 * there is no share to fill; the area is then a fixed 7cm.
 */
function Mockup({ files, fill }: { files: SheetFile[]; fill: boolean }) {
  const shown = files.filter((file) => file.url)
  const area = fill ? 'print:min-h-0 print:flex-1' : 'print:h-[7cm]'

  if (shown.length === 0) {
    return (
      <div className={cn('flex h-40 items-center justify-center rounded border-2 border-dashed border-line text-xs text-muted print:h-auto', area)}>
        No artwork uploaded
      </div>
    )
  }

  return (
    <div className={cn('grid gap-2', MOCKUP_GRID[Math.min(shown.length, 6)], area)}>
      {shown.map((file, index) => (
        <div key={index} className="relative min-h-0 min-w-0">
          <ArtworkPreview
            url={file.url!}
            name={file.name}
            mime={file.mime}
            className="h-full"
            imageClassName={cn(shown.length > 1 ? 'max-h-60' : 'max-h-80', 'print:h-full print:max-h-none print:w-full')}
          />
          {/* Numbered to match the file list and its QR below. */}
          {shown.length > 1 && (
            <span className="absolute left-1 top-1 rounded bg-navy px-1.5 text-2xs font-bold text-white">{index + 1}</span>
          )}
        </div>
      ))}
    </div>
  )
}

/** The file names and, on paper, one QR per file the floor scans to zoom into the full-size picture. */
function ArtworkDetails({ files }: { files: SheetFile[] }) {
  if (files.length === 0) return null
  const many = files.length > 1

  return (
    <div className="mt-2 flex shrink-0 items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-2xs font-bold uppercase text-muted">Customer artwork{many ? ` (${files.length} files)` : ''}</p>
        {files.map((file, index) => (
          <div key={index} className="min-w-0">
            {file.url ? (
              <a href={file.url} target="_blank" rel="noreferrer" className="block truncate text-xs font-semibold text-brand-600 underline">
                {many ? `${index + 1}. ` : ''}
                {file.name ?? 'Artwork file'}
                {fileSize(file.size) ? <span className="font-normal text-muted"> ({fileSize(file.size)})</span> : null}
              </a>
            ) : (
              <>
                <p className="truncate text-xs font-semibold">
                  {many ? `${index + 1}. ` : ''}
                  {file.name}
                </p>
                {/* Older orders recorded a name before upload existed;
                    there is genuinely no file to fetch. */}
                <p className="text-2xs text-muted">Filename recorded at order time - no file was uploaded</p>
              </>
            )}
          </div>
        ))}
      </div>
      {/* Print only. On screen you click the link, and a second QR next to
          the job QR is one that staff can scan by mistake. */}
      <div className="hidden shrink-0 gap-2 print:flex">
        {files.map(
          (file, index) =>
            file.url && (
              <div key={index} className="text-center">
                <QRCode value={file.url} size={many ? 52 : 64} />
                <p className="mt-0.5 text-2xs text-muted">{many ? `Artwork ${index + 1}` : 'Artwork'}</p>
              </div>
            ),
        )}
      </div>
    </div>
  )
}

/**
 * One product on the sheet: its pictures on the left, every option as its own
 * box (with the Product Options picture when the value has one) and the sizes
 * as a column, the CetakPro sheet the floor already knows. Neck colour, piping
 * and remark always print, blank or not, so the floor can write one in.
 *
 * `fill` (a one-item order): on paper the panel takes what the front page has
 * left and the pictures take all of it. A fixed picture cap with "never split"
 * on the panel threw the whole panel onto page 2 (Duncan's MPG-2026-00004
 * printed as three pages). Several items flow down the page instead, each
 * panel kept whole.
 */
function ItemPanel({
  item,
  groups,
  fill,
  number,
  showNames,
}: {
  item: OrderItem
  groups: OptionGroup[] | undefined
  fill: boolean
  /** "ITEM 1" on an order of several items, matching its name list on the back page; null on a one-item order. */
  number: number | null
  /** Print the Name & number box: some item on this order has names. */
  showNames: boolean
}) {
  const { files, options, freeText, sizes } = panelData(item, groups)

  return (
    <div
      className={cn(
        'mt-6 rounded-lg border-2 border-navy p-3 print:p-2',
        fill ? 'print:mt-0 print:flex print:min-h-0 print:flex-1 print:flex-col' : 'break-inside-avoid print:mt-3',
      )}
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-navy/30 pb-2 print:pb-1">
        <p className="min-w-0 text-base font-extrabold uppercase leading-tight">
          {number !== null && `Item ${number} - `}
          {item.product}
        </p>
        <p className="shrink-0 text-base font-extrabold">{item.qty} PCS</p>
      </div>

      <div
        className={cn(
          'mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_5.5rem] print:mt-2 print:grid-cols-[minmax(0,1fr)_10rem_5.5rem]',
          fill && 'print:min-h-0 print:flex-1 print:grid-rows-[minmax(0,1fr)]',
        )}
      >
        <div className="min-w-0 print:flex print:min-h-0 print:flex-col">
          <Mockup files={files} fill={fill} />
          <ArtworkDetails files={files} />
        </div>

        <div className="flex flex-col gap-1">
          {showNames && <SpecBox label="Name & number" value={namesNote(item)} />}
          {[...options, ...freeText].map((entry) => (
            <SpecBox key={entry.key} label={entry.label} value={entry.value} image={entry.image} />
          ))}
        </div>

        <table className="h-fit w-full border-collapse self-start border-2 border-navy text-center text-sm">
          <thead>
            <tr className="bg-navy text-2xs font-bold uppercase text-white">
              <th className="border border-navy px-1 py-1">Size</th>
              <th className="border border-navy px-1 py-1">Qty</th>
            </tr>
          </thead>
          <tbody>
            {sizes.map(([label, qty]) => (
              <tr key={label}>
                <td className="border border-navy/40 px-1 py-1 font-semibold">{label}</td>
                <td className="border border-navy/40 px-1 py-1 font-extrabold">{qty}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-navy">
              <td className="px-1 py-1 text-2xs font-bold uppercase">Total</td>
              <td className="px-1 py-1 font-extrabold">{item.qty}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function JobSheet() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const optionGroups = useOptionGroups()
  const isAdmin = user?.role === 'admin'
  // Each portal has its own sheet endpoint behind its own role gate.
  const prefix = isAdmin ? 'admin' : user?.role === 'partner' ? 'partner' : 'staff'

  const { data: payload, isLoading } = useQuery({
    queryKey: ['job-sheet', prefix, id],
    queryFn: async () =>
      (await api.get<{ data: Job; department_deadlines: DepartmentDeadline[] }>(`/${prefix}/jobs/${id}/sheet`)).data,
  })
  const job = payload?.data
  const deadlines = payload?.department_deadlines ?? []

  const toast = useToast()
  const queryClient = useQueryClient()
  // Admin only: book a late job's remaining sections again from today.
  const replan = useMutation({
    mutationFn: async () => (await api.post<{ message: string }>(`/admin/jobs/${id}/replan`)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['job-sheet'] })
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['section-load'] })
      toast.success('Re-planned', res.message)
    },
    onError: (err) => toast.error('Re-plan failed', errorMessage(err)),
  })

  // Admin only: send a pending order on from the office to its first section.
  const [proceedOpen, setProceedOpen] = useState(false)
  const proceed = useMutation({
    mutationFn: async () => (await api.post<{ message: string }>(`/admin/orders/${job?.order?.id}/proceed`)).data,
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      setProceedOpen(false)
      toast.success('Proceeded', res.message)
    },
    onError: (err) => toast.error('Could not send it on', errorMessage(err)),
  })

  // The office's Printed badge. On beforeprint rather than the button, so
  // Ctrl+P counts too; the server keeps only the first print.
  const unprinted = isAdmin && !!job && !job.sheet_printed_at
  useEffect(() => {
    if (!unprinted) return
    const record = () => {
      api
        .post(`/admin/jobs/${id}/printed`)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['job-sheet'] })
          queryClient.invalidateQueries({ queryKey: ['admin-jobs'] })
        })
        .catch(() => {})
    }
    window.addEventListener('beforeprint', record)
    return () => window.removeEventListener('beforeprint', record)
  }, [unprinted, id, queryClient])

  if (isLoading || !job) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LogoLoader tone="dark" />
      </div>
    )
  }

  const order = job.order
  // The partner's code in place of MPG while a partner holds the job. The QR
  // still carries qr_token, so a sheet printed before a hand-over still scans.
  const jobCode = job.display_code ?? job.code

  // Same derivation as the customer order page and the public tracker: every
  // department before the job's current one is done, the current one is live.
  // Stage logs only carry the department name, so the signature lookup matches
  // on that while the position match uses the id.
  const currentIdx =
    job.status === 'completed' ? deadlines.length : deadlines.findIndex((d) => d.department_id === job.current_department?.id)
  const logs = job.stage_logs ?? []
  const lastLog = (department: string, action: StageLog['action']) =>
    logs.filter((log) => log.department === department && log.action === action).reduce<StageLog | null>(
      (latest, log) => (!latest || log.at > latest.at ? log : latest),
      null,
    )
  const items = order?.items ?? []
  const named = items.filter((item) => (item.personalizations?.length ?? 0) > 0)
  // Several items: each is numbered on the front and on its name list, so a
  // list can be matched to its garment (Duncan 2026-11-03).
  const itemNumber = (item: OrderItem) => (items.length > 1 ? items.indexOf(item) + 1 : null)
  // One item: the front page is held to one A4 and the pictures fill it.
  const fill = (order?.items?.length ?? 0) <= 1

  // The sheet opens in a new tab, so history cannot say where it came from;
  // the opening link names it in ?from=. Anything else goes back to Jobs.
  const from = isAdmin ? searchParams.get('from') : null
  const back =
    from === 'orders'
      ? { to: '/admin/orders', label: 'Back to orders' }
      : from === 'pending'
        ? { to: '/admin/orders?tab=pending', label: 'Back to orders' }
        : from === 'order'
          ? { to: order ? `/admin/orders/${order.id}` : '/admin/orders', label: 'Back to order' }
          : from === 'issues'
            ? { to: '/admin/issues', label: 'Back to issues' }
            : { to: isAdmin ? '/admin/jobs' : user?.role === 'partner' ? '/partner' : '/staff/jobs', label: 'Back to jobs' }

  return (
    <div className="min-h-screen bg-app p-4 sm:p-6 print:bg-white print:p-0">
      <div className="no-print mx-auto mb-4 flex max-w-2xl flex-wrap items-center justify-between gap-2">
        <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline">
          <ArrowLeft className="size-4" /> {back.label}
        </Link>
        <div className="flex flex-wrap gap-2">
          {/* Money on screen for an admin only, and never on the paper. Only
              when it needs attention: paid is the normal case and just noise. */}
          {isAdmin && order?.payment_status !== 'paid' && (
            <PaymentBadge status={order?.payment_status} className="self-center" />
          )}
          {/* Only when the current section is past its booked day: books the
              remaining sections again from today, against current load. */}
          {isAdmin && job.status !== 'completed' && deadlines.find((d) => d.department_id === job.current_department?.id)?.late && (
            <Button variant="outline" loading={replan.isPending} onClick={() => replan.mutate()}>
              Re-plan from today
            </Button>
          )}
          {named.length > 0 && (
            <Button variant="outline" onClick={() => downloadDesignerExcel(job, order!)}>
              <FileSpreadsheet className="size-4" /> Designer Excel
            </Button>
          )}
          <Button onClick={() => window.print()}>
            <Printer className="size-4" /> Print job sheet
          </Button>
        </div>
      </div>

      {/* A pending order the floor has not received: the office prints the
          sheet here, then proceeds it. Never on the paper. */}
      {isAdmin && job.awaiting_proceed && (
        <div className="no-print mx-auto mb-4 flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-card border border-primary/60 bg-primary/20 p-3 text-primary">
          <div className="flex items-start gap-2 text-sm">
            <Inbox className="mt-0.5 size-4 shrink-0" />
            <div>
              <p className="font-semibold">Pending - {job.current_department?.name ?? 'the floor'} has not received this yet.</p>
              <p className="text-xs">
                {job.sheet_printed_at ? `Sheet printed ${formatDateTime(job.sheet_printed_at)}.` : 'Sheet not printed yet.'} Sent on
                after the production date, it is re-planned from today.
              </p>
            </div>
          </div>
          <Button onClick={() => setProceedOpen(true)}>
            <Send className="size-4" /> Proceed
          </Button>
          <ProceedConfirmDialog
            order={
              proceedOpen && order
                ? { number: order.number, production_date: order.production_date, section: job.current_department?.name }
                : null
            }
            loading={proceed.isPending}
            onClose={() => setProceedOpen(false)}
            onConfirm={() => proceed.mutate()}
          />
        </div>
      )}

      <div className="mx-auto max-w-2xl rounded-card border border-line bg-white p-4 sm:p-8 print:rounded-none print:border-0 print:p-0">
        {/* The front page: exactly one A4 on paper (279mm of the 281mm @page
            leaves, so rounding never spills a blank page) for a one-item order. Everything above
            the panels keeps its height and the panels share the rest; see
            ItemPanel. The name list starts page 2 below. */}
        <div className={cn('print:flex print:flex-col', fill && 'print:h-[279mm]')}>
          <div className="flex items-start justify-between border-b-2 border-navy pb-4 print:pb-2">
            <div>
              <p className="text-lg font-extrabold tracking-wide text-navy">MPG PRINTING</p>
              <p className="text-xs text-muted">Production Job Sheet</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-xl font-extrabold">{jobCode}</p>
              <UrgentBadge urgent={order?.is_urgent} variant="print" className="mt-1" />
              <p className="text-xs text-muted">Created {formatDate(order?.created_at)}</p>
            </div>
          </div>

          {/* A sheet printed before the cancel may still be on the floor; one
              printed after it has to be impossible to mistake for live work. */}
          {job.status === 'cancelled' && (
            <div className="mt-4 border-4 border-black p-3 text-center">
              <p className="text-2xl font-extrabold tracking-widest">CANCELLED</p>
              <p className="mt-1 text-xs font-semibold">
                {order?.cancel_reason ? `${order.cancel_reason.replace(/\.+$/, '')}. ` : ''}Stop work on this job and return the sheet to the office.
              </p>
            </div>
          )}

          {/* Above the QR and full width: the job code is what the system calls
              this job, this is what the people printing it call it, and it is
              the first thing anyone picking the sheet up looks for. Uppercased
              in CSS so the stored value stays exactly what was typed. */}
          <div className="mt-4 border-b border-navy/30 pb-3 print:mt-2 print:pb-2">
            <p className="text-2xs font-bold uppercase text-muted">Job name</p>
            <p className="text-lg font-extrabold uppercase leading-tight">{sheetTitle(order, jobCode)}</p>
          </div>

          <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6 print:mt-3 print:flex-row print:items-start print:gap-6">
            <div className="shrink-0 rounded-lg border-2 border-navy p-3">
              <QRCode value={job.qr_token} size={112} />
              <p className="mt-2 text-center font-mono text-2xs font-bold">{jobCode}</p>
            </div>
            <div className="grid w-full flex-1 grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2 print:grid-cols-3">
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Order</p>
                <p className="font-semibold">{order?.number}</p>
              </div>
              {/* Sits next to our own order number so the two references read
                  together. Conditional like the PIC below: an order without one
                  must not print an empty box. */}
              {order?.customer_ref && (
                <div>
                  <p className="text-2xs font-bold uppercase text-muted">Customer ref</p>
                  <p className="font-semibold">{order.customer_ref}</p>
                </div>
              )}
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Customer</p>
                <p className="font-semibold">{order?.agent?.name}</p>
                <p className="text-xs text-muted">{order?.agent?.company_name}</p>
              </div>
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Production date</p>
                <p className="font-semibold">{formatDate(order?.production_date)}</p>
              </div>
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Delivery due</p>
                <p className="font-semibold">{formatDate(order?.expected_completion_date)}</p>
              </div>
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Total pieces</p>
                <p className="font-semibold">{order?.total_pieces} pcs</p>
              </div>
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Location</p>
                <p className="font-semibold">{job.warehouse ? `External - ${job.warehouse.name}` : 'In-house'}</p>
              </div>
              <div>
                <p className="text-2xs font-bold uppercase text-muted">Delivery</p>
                <p className="font-semibold">{order?.delivery_method ?? '-'}</p>
                {order?.delivery_address && (
                  <p className="mt-0.5 text-xs">
                    {order.delivery_address.recipient_name} - {order.delivery_address.phone}
                    <br />
                    {order.delivery_address.line1}
                    {order.delivery_address.line2 ? `, ${order.delivery_address.line2}` : ''},{' '}
                    {order.delivery_address.postcode} {order.delivery_address.city}, {order.delivery_address.state}
                  </p>
                )}
              </div>
              {order?.sales_person && (
                <div>
                  <p className="text-2xs font-bold uppercase text-muted">Sales person (PIC)</p>
                  <p className="font-semibold">{order.sales_person}</p>
                </div>
              )}
            </div>
          </div>

          {order?.notes && (
            <p className="mt-4 rounded-lg border-2 border-dashed border-brand-500 p-3 text-sm">
              <span className="font-bold">Production notes:</span> {order.notes}
            </p>
          )}

          {/* One column per box at md and in print, so a five-section flow
              fills the row instead of leaving the empty seventh column a
              hardcoded grid-cols-7 did. Phones and tablets keep their fixed
              wrap. Every box is a flex column with the same fixed slots - a
              two-line title band, date, weekday, signature, and a footer pinned
              to the bottom - and the grid stretches them to one height, so the
              dates, weekdays and footers line up across the row whether a
              section name takes one line ("PRINT") or two ("HEAT PRESS"). */}
          <div
            className="mt-6 print:mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-(--stage-cols) print:grid-cols-(--stage-cols)"
            style={{ '--stage-cols': `repeat(${deadlines.length + 1}, minmax(0, 1fr))` } as CSSProperties}
          >
            {deadlines.map((d, index) => {
              const done = currentIdx >= 0 && index < currentIdx
              const current = index === currentIdx
              const issue = current && job.status === 'issue'
              const completed = done ? lastLog(d.department, 'complete') : null
              const started = current ? lastLog(d.department, 'start') : null

              return (
                <div
                  key={d.department_id}
                  className={cn(
                    'flex flex-col rounded-lg p-2 text-center',
                    done && 'border-2 border-success/60 bg-success/10',
                    current && !issue && 'border-2 border-pending/60 bg-pending/10',
                    issue && 'border-2 border-danger/60 bg-danger/10',
                    !done && !current && 'border border-line',
                  )}
                >
                  {/* Always two lines tall. 2lh is two of this text's own line
                      heights, so it holds if the size ever changes. Name only:
                      a status icon in here took the width "1. DESIGN" needs and
                      split the number from the name. */}
                  <p
                    className={cn(
                      'flex min-h-[2lh] items-center justify-center text-2xs font-bold uppercase',
                      done ? 'text-success' : issue ? 'text-danger' : current ? 'text-pending' : 'text-muted',
                    )}
                  >
                    {index + 1}. {d.department}
                  </p>
                  <p className="mt-0.5 text-2xs font-semibold">{formatDate(d.date)}</p>
                  {/* The floor schedules by day of the week, not by date. Plain
                      foreground rather than text-muted: this has to survive a
                      mono laser. */}
                  <p className="text-2xs">{formatWeekday(d.date)}</p>
                  {completed ? (
                    // The digital signature: who completed the stage, and when.
                    <div className="mt-1 flex h-8 flex-col items-center justify-center">
                      <p className="max-w-full truncate text-2xs font-semibold text-success" title={workedBy(completed)}>{workedByShort(completed)}</p>
                      {/* The actual date stamped on the section's plan when it
                          completed, falling back to the log for older jobs. */}
                      <p className="text-2xs text-success">{formatDate(d.completed_at ?? completed.at)}</p>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        'mt-1 flex h-8 flex-col items-center justify-end border-b border-dashed pb-0.5',
                        issue ? 'border-danger/60' : current ? 'border-pending/60' : 'border-line',
                      )}
                    >
                      {started && <p className="max-w-full truncate text-2xs text-pending">{started.staff}</p>}
                    </div>
                  )}
                  {/* Two lines tall with the status on the bottom line, like the
                      title band above: at seven columns a box has about 60px, so
                      "In progress" and "Issue flagged" wrap, and without the band
                      their footer climbs out of line with "Sign / scan".
                      box-content keeps pt-1 outside the two lines: under the
                      default border-box a wrapped label outgrew the band by the
                      padding and sat 5px higher than the rest. No icon -
                      the box colour, the word and the signature already say it,
                      and a spinner glyph means nothing on paper. */}
                  <p
                    className={cn(
                      'mt-auto box-content flex min-h-[2lh] items-end justify-center pt-1 text-2xs',
                      done
                        ? 'font-semibold text-success'
                        : issue || (current && d.late)
                          ? 'font-semibold text-danger'
                          : current
                            ? 'font-semibold text-pending'
                            : 'text-muted',
                    )}
                  >
                    {/* Late is printed as a word, never only a tint: the sheet
                        goes through a mono laser. */}
                    {done
                      ? d.late ? 'Done late' : 'Done'
                      : issue
                        ? 'Issue flagged'
                        : current
                          ? d.late ? 'LATE' : job.status === 'in_progress' ? 'In progress' : 'Queued'
                          : 'Sign / scan'}
                  </p>
                </div>
              )
            })}
            <div className="flex flex-col rounded-lg border-2 border-dashed border-danger p-2 text-center">
              <p className="flex min-h-[2lh] items-center justify-center text-2xs font-bold uppercase">Delivery due</p>
              <p className="mt-0.5 text-2xs font-semibold">{formatDate(order?.expected_completion_date)}</p>
              <p className="text-2xs">{formatWeekday(order?.expected_completion_date)}</p>
              <div className="mt-1 h-8 border-b border-dashed border-line" />
              <p className="mt-auto box-content flex min-h-[2lh] items-end justify-center pt-1 text-2xs text-muted">Dispatch</p>
            </div>
          </div>

          {/* One panel per product, laid out like the CetakPro sheet the floor
              already knows (client feedback 2026-10-05): the mockup large, then
              one box per option with its picture, then sizes as a column. The
              floor reads pictures and short words; the old " / " spec line was
              the one part of the sheet they called cramped. The panels sit
              below the stage grid so a tall mockup never pushes the stages onto
              page 2. A one-item order is held to one A4 and its pictures
              fill what is left; several items flow down the page. */}
          <div className={cn('print:mt-2', fill && 'print:flex print:min-h-0 print:flex-1 print:flex-col')}>
            {order?.items?.map((item) => (
              <ItemPanel
                key={item.id}
                item={item}
                groups={optionGroups.data}
                fill={fill}
                number={itemNumber(item)}
                showNames={named.length > 0}
              />
            ))}
          </div>

          <p className="mt-5 text-center print:mt-2 text-2xs text-muted">
            {/* The isAdmin guard is now belt-and-braces: a staff or partner
                payload no longer carries a total at all. */}
            Scan the QR code with your staff account to update this job's stage.{isAdmin ? ` Total ${money(order?.total)}.` : ''}
          </p>
          <p className="mt-1 text-center text-2xs text-muted">
            Please ensure all logos and designs are sharp; we are not responsible if the supplied design is already blurry.
          </p>
        </div>

        {/* The back page, as the client asked: a long list on page 1 pushed
            the stages and the mockup off it. One list per product, numbered
            from 1 each, because a merged list never said which garment a name
            belongs to. The job code and name repeat so a loose back page can
            be matched to its front. */}
        {named.length > 0 && (
          <div className="mt-8 border-t-2 border-navy pt-4 print:mt-0 print:break-before-page print:border-t-0 print:pt-0">
            <div className="flex items-baseline justify-between gap-3 border-b-2 border-navy pb-2">
              <p className="text-sm font-extrabold uppercase">Name & number list</p>
              <p className="text-right text-xs font-semibold">
                <span className="font-mono">{jobCode}</span> / <span className="uppercase">{sheetTitle(order, jobCode)}</span>
              </p>
            </div>
            {named.map((item) => (
              // No break-inside-avoid here: a list longer than a page was
              // thrown whole onto the next one, leaving the heading alone on
              // a blank page. Rows stay whole and the header repeats instead.
              <div key={item.id} className="mt-4">
                {/* Which garment this list is for: the item number on the front
                    and its options, so short sleeve, long sleeve and Muslimah
                    lists cannot be mixed up. */}
                {items.length > 1 && (
                  <>
                    <p className="break-after-avoid text-xs font-bold uppercase">
                      Item {itemNumber(item)} - {item.product} - {item.personalizations!.length} names
                    </p>
                    <p className="break-after-avoid text-xs uppercase">{specSummary(item.spec, optionGroups.data, FREE_TEXT_SPEC_KEYS)}</p>
                  </>
                )}
                {/* Which font the size layout uses (client request 2026-10-14). */}
                <p className="break-after-avoid text-xs">
                  <span className="font-bold">Font:</span>{' '}
                  {(item.fonts?.length ?? 0) > 0 ? item.fonts!.map((file) => file.name ?? 'font file').join(', ') : 'not sent - check with the office'}
                </p>
                <table className="mt-1 w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b-2 border-navy text-left text-xs">
                      <th className="px-2 py-1 font-bold">#</th>
                      <th className="px-2 py-1 font-bold">Size</th>
                      <th className="px-2 py-1 font-bold">Name</th>
                      <th className="px-2 py-1 font-bold">Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.personalizations!.map((p, n) => (
                      <tr key={n} className="break-inside-avoid border-b border-line">
                        <td className="px-2 py-1">{n + 1}</td>
                        <td className="px-2 py-1">{p.size}</td>
                        <td className="px-2 py-1 font-semibold">{p.name}</td>
                        <td className="px-2 py-1 font-semibold">{p.number}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            <p className="mt-2 text-2xs text-muted">Ensure the name and the number on the garment match (name 1 goes with no 1).</p>
          </div>
        )}
      </div>
    </div>
  )
}
